import { Loader2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui";
import type { DiagnosisStatus } from "~/features/dataImportProjects/hooks/useDataImportJobDiagnosis";
import type { DipPipelineDryRunResultTarget, DipRunHistoryRow } from "~/features/dataImportProjects/types";
import { cn } from "~/lib/utils";
import { DipReadOnlyTooltip } from "./DipReadOnlyTooltip";
import { DipRunHistory } from "./DipRunHistory";
import { DipRunRailScope } from "./DipRunRailScope";

// ─── Types ──────────────────────────────────────────────────────────────────

interface DipRunRailJob {
  id: string;
  name: string;
}

interface DipRunRailProps {
  open: boolean;
  mode: "run" | "diagnosis";
  readOnly: boolean;
  onClose: () => void;
  running: boolean;
  /** Which action is in progress, so the matching button shows "Running…". */
  runningAction?: "run" | "dryrun" | null;
  beJobs: DipRunRailJob[];
  bcrJobs: DipRunRailJob[];
  onRun: (
    action: "run" | "dryrun",
    beJobIds: string[],
    bcrJobIds: string[],
    opts: { autoApprove: boolean; logLevel: string },
  ) => void;
  diagnosisStatus?: DiagnosisStatus;
  onRunDiagnosis?: () => void;
  historyRows: DipRunHistoryRow[];
  onOpenLogs: (jobRunId: string, dryRun?: boolean) => void;
  onOpenDiagnosis: (diagnosisId: string) => void;
  onOpenDryRunResult?: (target: DipPipelineDryRunResultTarget) => void;
}

type Scope = "all" | "step1" | "step2" | "custom";
type Tab = "run" | "history";

// ─── Main component ──────────────────────────────────────────────────────────

export function DipRunRail({
  open,
  mode,
  readOnly,
  onClose,
  running,
  runningAction,
  beJobs,
  bcrJobs,
  onRun,
  diagnosisStatus,
  onRunDiagnosis,
  historyRows,
  onOpenLogs,
  onOpenDiagnosis,
  onOpenDryRunResult,
}: DipRunRailProps) {
  const isDiagnosis = mode === "diagnosis";
  const { t } = useTranslation();

  const [tab, setTab] = useState<Tab>("run");
  const [scope, setScope] = useState<Scope>("all");
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set([...beJobs.map((j) => j.id), ...bcrJobs.map((j) => j.id)]),
  );
  const [autoApprove, setAutoApprove] = useState(false);
  const [logLevel, setLogLevel] = useState("info");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const beJobIdList = useMemo(() => beJobs.map((j) => j.id), [beJobs]);
  const bcrJobIdList = useMemo(() => bcrJobs.map((j) => j.id), [bcrJobs]);

  // History is scoped to the rail's mode: run mode shows pipeline runs, diagnosis mode shows diagnoses.
  const shownHistory = useMemo(
    () => historyRows.filter((row) => (isDiagnosis ? row.kind === "diagnosis" : row.kind === "pipeline")),
    [historyRows, isDiagnosis],
  );

  // Re-initialize selection when job lists change and scope is "all"
  useEffect(() => {
    if (scope === "all") setSelected(new Set([...beJobIdList, ...bcrJobIdList]));
  }, [beJobIdList, bcrJobIdList, scope]);

  // Close the rail on Escape (it is a non-modal panel without a built-in dismiss).
  // Skip when an open Select/AlertDialog already consumed the key (radix calls preventDefault)
  // or while the auto-approve confirm dialog is open, so Escape dismisses those first.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented && !confirmOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, confirmOpen, onClose]);

  // Reset to the primary (action) tab when the rail opens or its mode changes.
  // The run/history tab selection is intentionally NOT remembered across opens or
  // between run and diagnosis modes.
  useEffect(() => {
    if (open) setTab("run");
  }, [open, mode]);

  useEffect(() => {
    if (readOnly) setConfirmOpen(false);
  }, [readOnly]);

  const applyScope = (newScope: Scope) => {
    setScope(newScope);
    if (newScope === "all") {
      setSelected(new Set([...beJobIdList, ...bcrJobIdList]));
    } else if (newScope === "step1") {
      setSelected(new Set(beJobIdList));
    } else if (newScope === "step2") {
      setSelected(new Set(bcrJobIdList));
    }
    // "custom" is set implicitly via toggleJob
  };

  const toggleJob = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    setScope("custom");
  };

  const selectedBe = beJobs.filter((j) => selected.has(j.id)).map((j) => j.id);
  const selectedBcr = bcrJobs.filter((j) => selected.has(j.id)).map((j) => j.id);
  const count = selectedBe.length + selectedBcr.length;

  const handleDryRun = () => {
    if (readOnly) return;
    onRun("dryrun", selectedBe, selectedBcr, { autoApprove, logLevel });
  };

  const handleRun = () => {
    if (readOnly) return;
    if (autoApprove) {
      setConfirmOpen(true);
    } else {
      onRun("run", selectedBe, selectedBcr, { autoApprove, logLevel });
    }
  };

  const handleConfirmRun = () => {
    if (readOnly) return;
    setConfirmOpen(false);
    onRun("run", selectedBe, selectedBcr, { autoApprove, logLevel });
  };

  return (
    <>
      {/* Non-modal docked rail — no scrim so the user can interact with the graph */}
      <div
        className={cn(
          "fixed right-0 top-0 z-40 flex h-full w-[392px] flex-col border-l border-border-default bg-bg-page shadow-lg transition-transform duration-300",
          open ? "translate-x-0" : "translate-x-full",
        )}
        aria-hidden={!open}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border-default px-4 py-3">
          <div className="flex items-center gap-2">
            <span
              className={cn("size-2.5 rounded-full", isDiagnosis ? "bg-score-b" : "bg-primary-500")}
              aria-hidden="true"
            />
            <span className="text-[15px] font-semibold text-fg-1">
              {isDiagnosis
                ? t("feature.dataImportProjects.diagnosis.label")
                : t("feature.dataImportProjects.v2.run.title")}
            </span>
          </div>
          <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label={t("ui.close")}>
            <X className="size-4" />
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)} className="flex min-h-0 flex-1 flex-col">
          <div className="shrink-0 border-b border-border-default px-4 pt-3 pb-0">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="run">{t("ui.run")}</TabsTrigger>
              <TabsTrigger value="history" className="gap-1.5">
                {t("ui.history")}
                {shownHistory.length > 0 && <Badge className="px-1.5 py-0 text-[11px]">{shownHistory.length}</Badge>}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Run tab */}
          <TabsContent value="run" className="mt-0 flex min-h-0 flex-1 flex-col overflow-auto">
            {isDiagnosis ? (
              <div className="flex flex-col gap-5 p-4">
                {/* Diagnosis subtitle — explains that the whole project is always diagnosed */}
                <p className="text-[13px] text-fg-2">{t("feature.dataImportProjects.v2.run.diagnosisSubtitle")}</p>

                {/* Locked scope selector + job lists (display-only, whole-project view) */}
                <DipRunRailScope
                  readOnly
                  scope="all"
                  selected={new Set([...beJobIdList, ...bcrJobIdList])}
                  beJobs={beJobs}
                  bcrJobs={bcrJobs}
                  applyScope={applyScope}
                  toggleJob={toggleJob}
                />

                {/* Run diagnosis button */}
                <div className="[&>span]:w-full">
                  <DipReadOnlyTooltip
                    disabled={readOnly}
                    ariaLabel={t("feature.dataImportProjects.v2.run.diagnosisAction")}
                  >
                    <Button
                      type="button"
                      size="lg"
                      className="w-full bg-score-b text-white"
                      disabled={readOnly || diagnosisStatus === "invoking" || diagnosisStatus === "polling"}
                      onClick={() => {
                        if (readOnly) return;
                        onRunDiagnosis?.();
                      }}
                    >
                      {diagnosisStatus === "invoking" || diagnosisStatus === "polling" ? (
                        <span className="flex items-center gap-1.5">
                          <Loader2 className="size-4 animate-spin" />
                          {t("feature.dataImportProjects.v2.run.action.running")}
                        </span>
                      ) : (
                        t("feature.dataImportProjects.v2.run.diagnosisAction")
                      )}
                    </Button>
                  </DipReadOnlyTooltip>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-5 p-4">
                {/* Subtitle */}
                <p className="text-[13px] text-fg-2">{t("feature.dataImportProjects.v2.run.subtitle")}</p>

                {/* Scope segmented control + BE/BCR job lists (interactive) */}
                <DipRunRailScope
                  readOnly={readOnly}
                  scope={scope}
                  selected={selected}
                  beJobs={beJobs}
                  bcrJobs={bcrJobs}
                  applyScope={applyScope}
                  toggleJob={toggleJob}
                  beStepOption={
                    /* Step 1 per-step option: auto-commit (UI-only, always on) */
                    <div className="ml-1 mt-2 flex items-center justify-between gap-3">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Label className="text-[13px] text-fg-2 cursor-default underline decoration-dotted underline-offset-2">
                            {t("feature.dataImportProjects.v2.run.option.autoCommit")}
                          </Label>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-xs">
                          {t("feature.dataImportProjects.v2.run.option.autoCommitNote")}
                        </TooltipContent>
                      </Tooltip>
                      <Switch checked disabled />
                    </div>
                  }
                  bcrStepOption={
                    /* Step 2 per-step option: auto-approve */
                    <div className="ml-1 mt-2 flex items-center justify-between gap-3">
                      <Label className="text-[13px] text-fg-2">
                        {t("feature.dataImportPipelineJob.autoApproveChangeRequests")}
                      </Label>
                      <Switch checked={autoApprove} onCheckedChange={(v) => setAutoApprove(v)} />
                    </div>
                  }
                />

                {/* Options section */}
                <div className="flex flex-col gap-3">
                  <span className="text-[12px] font-medium text-fg-3 uppercase tracking-wide">{t("ui.options")}</span>
                  {/* Log level */}
                  <div className="flex items-center justify-between gap-3">
                    <Label htmlFor="run-rail-log-level" className="text-[13px] text-fg-2 shrink-0">
                      {t("ui.logLevel")}
                    </Label>
                    <Select value={logLevel} onValueChange={setLogLevel} disabled={readOnly}>
                      <SelectTrigger id="run-rail-log-level" className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="error">{t("ui.logLevelError")}</SelectItem>
                        <SelectItem value="warn">{t("ui.logLevelWarn")}</SelectItem>
                        <SelectItem value="info">{t("ui.logLevelInfo")}</SelectItem>
                        <SelectItem value="debug">{t("ui.logLevelDebug")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Action row — run is the primary, emphasized action */}
                <div className="flex items-stretch gap-2">
                  <div className="flex flex-1 [&>span]:flex-1">
                    <DipReadOnlyTooltip disabled={readOnly} ariaLabel={t("ui.dryRun")}>
                      <Button
                        type="button"
                        variant="outline"
                        size="lg"
                        className="flex-1"
                        disabled={readOnly || running || count === 0}
                        onClick={handleDryRun}
                      >
                        {runningAction === "dryrun" ? (
                          <span className="flex items-center gap-1.5">
                            <Loader2 className="size-4 animate-spin" />
                            {t("feature.dataImportProjects.v2.run.action.running")}
                          </span>
                        ) : (
                          t("ui.dryRun")
                        )}
                      </Button>
                    </DipReadOnlyTooltip>
                  </div>
                  <div className="flex flex-[1.6] [&>span]:flex-1">
                    <DipReadOnlyTooltip disabled={readOnly} ariaLabel={t("ui.run")}>
                      <Button
                        type="button"
                        size="lg"
                        className="flex-[1.6] text-[15px] font-bold"
                        disabled={readOnly || running || count === 0}
                        onClick={handleRun}
                      >
                        {runningAction === "run" ? (
                          <span className="flex items-center gap-1.5">
                            <Loader2 className="size-4 animate-spin" />
                            {t("feature.dataImportProjects.v2.run.action.running")}
                          </span>
                        ) : (
                          t("ui.run")
                        )}
                      </Button>
                    </DipReadOnlyTooltip>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* History tab */}
          <TabsContent value="history" className="mt-0 flex-1 overflow-auto p-4">
            <DipRunHistory
              rows={shownHistory}
              compact
              onOpenLogs={onOpenLogs}
              onOpenDiagnosis={onOpenDiagnosis}
              onOpenDryRunResult={onOpenDryRunResult}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Auto-approve confirmation dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("feature.dataImportProjects.v2.run.confirmAutoApprove.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("feature.dataImportProjects.v2.run.confirmAutoApprove.body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("ui.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRun}>
              {t("feature.dataImportProjects.v2.run.confirmAutoApprove.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
